/** @format */
import { useInfiniteQuery } from '@tanstack/react-query';
import { useContext } from 'react';

import { ModelSearchResultsType } from '@toucan/common/services/search/constants';
import { getModelSearchResults } from '@toucan/common/services/search/model/helpers';
import {
    ModelSearchResultsSearchParams,
    Query,
} from '@toucan/common/services/search/types';

import { Milliseconds } from '@toucan/client/global/application';
import { MarketContext } from '@toucan/client/global/market';

import { UseModelInfiniteResultsResult } from '@toucan/client/features/search/components/model-search-results/hooks/use-model-search-results/hooks/use-model-infinite/types';
import { useShouldUseSemanticSearch } from '@toucan/client/features/search/components/model-search-results/hooks/use-should-use-semantic-search';
import { SearchResultsType } from '@toucan/client/features/search/components/model-search-results/types';

export const useModelInfiniteResults = (
    queryKey: string,
    isInfiniteScroll: boolean,
    resultsPerPage: number,
    baseQuery: Partial<Query>,
    searchParams: ModelSearchResultsSearchParams,
    type: ModelSearchResultsType
): UseModelInfiniteResultsResult => {
    const { activeMarket } = useContext(MarketContext);
    const shouldUseSemanticSearch = useShouldUseSemanticSearch(type);

    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- Legacy code: do not use as an example.
    const query = useInfiniteQuery({
        enabled: isInfiniteScroll,
        queryKey: ['infiniteScrollModelSearchResults', type, queryKey],
        queryFn: async ({ pageParam = 0 }) => {
            const start = pageParam * resultsPerPage;
            const rows = resultsPerPage;

            const results = await getModelSearchResults(
                baseQuery,
                searchParams,
                start,
                rows,
                activeMarket,
                { type, shouldUseSemanticSearch }
            );

            return results;
        },
        getNextPageParam: (
            lastPage: SearchResultsType,
            allPages: SearchResultsType[]
        ) => {
            const currentPageIndex = allPages.length - 1;
            const currentTotalResults = allPages.reduce(
                (acc, page) => acc + page.resultCount,
                0
            );
            const nextPage = currentPageIndex + 1;

            return currentTotalResults < lastPage.totalResults
                ? nextPage
                : undefined;
        },
        refetchOnWindowFocus: false,
        staleTime: Milliseconds.FIVE_MINUTES,
    });

    const results = query?.data?.pages.reduce(
        (acc, page) => {
            return {
                results: [...acc.results, ...page.results],
                resultCount: acc.resultCount + page.resultCount,
                totalResults: page.totalResults,
                spellcheck: page.spellcheck,
            };
        },
        {
            results: [],
            resultCount: 0,
            totalResults: 0,
            spellcheck: { suggestions: [] },
            facets: [],
        } as SearchResultsType
    );

    return { ...query, data: results };
};
